import { Ace as AceNamespace } from 'ace-builds';

/**
 * Finds the range of an image tag if the cursor (or cursor starting position when a range is
 * selected) is currently within an image tag, otherwise it returns null
 * @param {AceNamespace.Editor} editor - an Ace editor
 * @returns {AceNamespace.Range | undefined}
 */
export const getCurrentElementRange = (editor: AceNamespace.Editor) => {
  // editor.selection.getRange() works best for both dblclick location and a selected range. It
  // returns an AceNamespace.Range (using ordinal integers) for the range selected. In the case of
  // dblclick the Range start and end Position will be equal. Do not use editor.selection.getCursor()
  // or editor.selection.getSelectionLead() because they will each return the current position of the
  // cursor (which will be different if you make a selction from rtl vs ltr)
  const cursor = editor.selection.getRange().start;
  const doc = editor.session.getDocument();
  const rows = editor.session.getLength();

  let eltStart: AceNamespace.Point | null = null;

  // First, find the nearest element start
  // eslint-disable-next-line no-labels, no-restricted-syntax
  findNodeStart: {
    for (let currRow = cursor.row; currRow >= 0; currRow--) {
      const currLine = doc.getLine(currRow);

      // On cursor row: start from before cursor position
      // On previous rows: search entire line from end
      const startCol = currRow === cursor.row ? cursor.column - 1 : currLine.length - 1;

      for (let currCol = startCol; currCol >= 0; currCol--) {
        if (currLine.charAt(currCol) === '<') {
          eltStart = { row: currRow, column: currCol };
          // eslint-disable-next-line no-labels
          break findNodeStart;
        }
        if (currLine.charAt(currCol) === '>') {
          // Found closing tag before opening tag - cursor is outside any element
          return undefined;
        }
      }
    }
  }

  // Early exit if no element start found
  if (!eltStart) {
    return undefined;
  }

  // Now parse element type
  let eltType = '';
  const eltLine = doc.getLine(eltStart.row);

  // Parse element type manually instead of using getWordRange which can be fooled
  let tagStart = eltStart.column + 1; // Skip the '<' character
  let tagEnd = tagStart;

  // Find the end of the tag name (stop at space, '>', '/', or newline)
  while (tagEnd < eltLine.length) {
    const char = eltLine.charAt(tagEnd);
    if (char === ' ' || char === '>' || char === '/' || char === '\n' || char === '\t') {
      break;
    }
    tagEnd++;
  }

  // Extract and validate the tag name
  if (tagEnd > tagStart) {
    eltType = eltLine.substring(tagStart, tagEnd).toLowerCase();
  }

  // Only proceed if it's an img element
  if (eltType !== 'img') {
    return undefined;
  }

  // Find the element end (only for confirmed img elements)
  let eltEnd: AceNamespace.Point | null = null;

  // eslint-disable-next-line no-labels, no-restricted-syntax
  findNodeEnd: {
    // Start searching from the tag start position, not cursor position
    let startRow = eltStart.row;
    let startCol = eltStart.column;

    for (let currRow = startRow; currRow < rows; currRow++) {
      const currLine = doc.getLine(currRow);
      const colStart = currRow === startRow ? startCol : 0;

      for (let currCol = colStart; currCol < currLine.length; currCol++) {
        if (currLine.charAt(currCol) === '>') {
          eltEnd = { row: currRow, column: currCol + 1 };
          // eslint-disable-next-line no-labels
          break findNodeEnd;
        }
        if (currLine.charAt(currCol) === '<' && currRow !== startRow && currCol !== startCol) {
          // unexpected element initiator - not in the img tag
          // eslint-disable-next-line no-labels
          break findNodeEnd;
        }
      }
    }
  }

  if (eltStart && eltEnd) {
    return {
      start: { ...eltStart },
      end: { ...eltEnd },
    } as AceNamespace.Range;
  }

  return undefined;
};

/**
 * Finds the range of a parent anchor tag surrounding the provided , otherwise it returns null
 * @param {AceNamespace.Editor} editor - an Ace editor
 * @param {AceNamespace.Editor} editor - an Ace editor
 * @returns {AceNamespace.Range | undefined}
 */
export const getParentElementRange = (
  editor: AceNamespace.Editor,
  currentRange?: AceNamespace.Range,
) => {
  const cursorStart = currentRange?.start || editor.selection.getRange().start;
  const cursorEnd = currentRange?.end || editor.selection.getRange().end;
  const doc = editor.session.getDocument();
  const rows = editor.session.getLength();

  let eltStart: AceNamespace.Point | null = null;
  let eltEnd: AceNamespace.Point | null = null;
  let eltType = '';

  // eslint-disable-next-line no-labels, no-restricted-syntax
  findNodeStart: {
    for (let currRow = cursorStart.row; currRow >= 0; currRow--) {
      const currLine = doc.getLine(currRow);
      for (let currCol = cursorStart.column; currCol >= 0; currCol--) {
        if (
          currLine.charAt(currCol) === '<' &&
          !(currRow === cursorStart.row && currCol === cursorStart.column)
        ) {
          eltStart = { row: currRow, column: currCol };
          // eslint-disable-next-line no-labels
          break findNodeStart;
        }
      }
    }
  }

  // if an opening element character (<) is found, then inspect the next word for the element type
  if (eltStart) {
    const eltLine = doc.getLine(eltStart.row);
    const [eltTypeRow, eltTypeCol] =
      eltStart.column >= eltLine.length - 1
        ? [eltStart.row + 1, 0]
        : [eltStart.row, eltStart.column + 1];

    const eltTypeRange = editor.session.getWordRange(eltTypeRow, eltTypeCol);
    const eltTypeLine = doc.getLine(eltTypeRange.start.row);

    eltType = eltTypeLine
      .substring(eltTypeRange.start.column, eltTypeRange.end.column)
      .toLowerCase();
  }

  // if a qualifying element type has been found, find the position of the element terminator
  if (eltType === 'a') {
    // eslint-disable-next-line no-labels, no-restricted-syntax
    findNodeEnd: {
      for (let currRow = cursorEnd.row; currRow < rows; currRow++) {
        const currLine = doc.getLine(currRow).trim();
        for (let currCol = cursorEnd.column; currCol < currLine.length; currCol++) {
          if (
            currLine.charAt(currCol) === '>' &&
            currLine.charAt(currCol - 1) === 'a' &&
            currLine.charAt(currCol - 2) === '/' &&
            currLine.charAt(currCol - 3) === '<' &&
            !(currRow === cursorEnd.row && currCol === cursorEnd.column)
          ) {
            eltEnd = { row: currRow, column: currCol + 1 };
            // eslint-disable-next-line no-labels
            break findNodeEnd;
          }
        }
      }
    }
  }

  if (eltStart && eltEnd) {
    return {
      start: { ...eltStart },
      end: { ...eltEnd },
    } as AceNamespace.Range;
  }

  return undefined;
};

export function getCurrentElement<T extends HTMLElement>(
  editor: AceNamespace.Editor,
  eltRange: AceNamespace.Range,
): T {
  const eltString = editor.session.getTextRange(eltRange);
  const eltContainer = document.createElement('div');
  eltContainer.innerHTML = eltString;

  // Coerce received Node into HTMLElement
  return eltContainer.childNodes[0] as T;
}

export const isAnchorElement = (element?: HTMLElement): element is HTMLAnchorElement => {
  return element?.tagName === 'A';
};

export const isValidAnchorElement = (element?: HTMLElement): boolean => {
  return isAnchorElement(element) && element?.hasAttribute('href');
};

export const isImgElement = (element?: HTMLElement): element is HTMLImageElement => {
  return element?.tagName === 'IMG';
};

export const isValidImgElement = (element?: HTMLElement): boolean => {
  return isImgElement(element) && element?.hasAttribute('src');
};
